import qs from 'qs';
import type { RouteLocationNormalized, RouteLocationRaw, RouteRecordRaw } from 'vue-router';

import type {
    PasswordCredentialsTokenRequest,
    AuthorizationCodeRequest,
    AuthorizationCodeTokenRequest
} from '@@/modules/auth/runtime/types';
import type {
    ConfirmEmailFormDTO,
    ForgotPasswordFormDTO,
    RegisterFormDTO,
    RegisterResultDTO,
    ResetPasswordFormDTO
} from '@/types/api/auth';
import { publishAuthSessionEvent } from '@/utils/auth-session-channel';
import { HOME } from '@/constants/routes';
import type { UserInfo } from '@/types/auth';
import type { ApiFetchOptions } from '@/types/common';
import type { PageRouteMeta } from '@/types/page-route-meta';
import {
    AuthGrantTypes,
    AUTH_CODE_RESPONSE_TYPE,
    AUTH_FORM_URL_ENCODED_CONTENT_TYPE,
    AuthSessionEventTypes
} from '@/constants/auth';

const AUTH_CODE_VERIFIER_STORAGE_KEY = 'code_verifier';
const AUTH_RANDOM_CHARACTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

type LoginAsUserTokenRequest = {
    client_id: string;
    grant_type: typeof AuthGrantTypes.LoginAsUser;
    username: string;
    return_to: string;
    scope: string;
};

type LoginBackAsAdminTokenRequest = {
    client_id: string;
    grant_type: typeof AuthGrantTypes.LoginBackAsAdmin;
    scope: string;
};

type AuthTokenResponseData = { access_token: string; refresh_token?: string; expires_in: number };
type SignOutOptions = { revokeRemoteSession?: boolean; redirect?: string };

export const useAuth = () => {
    const userSession = useUserSessionStore();
    const router = useRouter();
    const requestURL = useRequestURL();

    const authBaseURL = useRuntimeConfig().public.authBaseURL;
    const options = useRuntimeConfig().public.auth;

    async function signIn(username: string, password: string, rememberMe: boolean, redirect?: RouteLocationRaw) {
        const data: PasswordCredentialsTokenRequest = {
            client_id: options.clientId,
            grant_type: AuthGrantTypes.Password,
            username: username,
            password: password,
            scope: options.scope
        };

        await _signIn(data, rememberMe, redirect);
    }

    async function signInAsUser(username: string, redirect?: RouteLocationRaw) {
        const data: LoginAsUserTokenRequest = {
            client_id: options.clientId,
            grant_type: AuthGrantTypes.LoginAsUser,
            username: username,
            return_to: router.currentRoute.value.path,
            scope: options.scope
        };

        await _signIn(data, false, redirect ?? HOME);
    }

    async function signInBackAsAdmin() {
        const data: LoginBackAsAdminTokenRequest = {
            client_id: options.clientId,
            grant_type: AuthGrantTypes.LoginBackAsAdmin,
            scope: options.scope
        };

        await _signIn(data, false, userSession.userInfo?.return_to);
    }

    async function signInProvider(provider: string) {
        const codeVerifier = generateRandomString(64);
        const codeChallenge = await generateCodeChallenge(codeVerifier);

        sessionStorage.setItem(AUTH_CODE_VERIFIER_STORAGE_KEY, codeVerifier);

        const params: AuthorizationCodeRequest = {
            client_id: options.clientId,
            response_type: AUTH_CODE_RESPONSE_TYPE,
            code_challenge: codeChallenge,
            code_challenge_method: 'S256',
            provider,
            scope: options.scope,
            redirect_uri: requestURL.origin + requestURL.pathname
        };

        // Using navigateTo as some providers don't support CORS, so fetch won't work
        // An example of such provider is GitHub - https://github.com/isaacs/github/issues/330
        await navigateTo(authBaseURL + options.authorizeURL + '?' + new URLSearchParams(params).toString(), {
            external: true
        });
    }

    async function _signIn(
        data: PasswordCredentialsTokenRequest | LoginAsUserTokenRequest | LoginBackAsAdminTokenRequest,
        rememberMe: boolean,
        redirect?: RouteLocationRaw
    ) {
        const response = await fetch<AuthTokenResponseData>(options.tokenURL, {
            body: qs.stringify(data),
            headers: { 'Content-Type': AUTH_FORM_URL_ENCODED_CONTENT_TYPE },
            method: 'POST'
        });

        userSession.setTokens(response.access_token, response.refresh_token, response.expires_in, rememberMe);

        await updateUserInfo();
        publishAuthSessionEvent(AuthSessionEventTypes.SignedIn);

        if (redirect) {
            await navigateTo(redirect);
        }
    }

    async function requestToken(authorizationCode: string, redirect?: string) {
        const data: AuthorizationCodeTokenRequest = {
            client_id: options.clientId,
            grant_type: AuthGrantTypes.AuthorizationCode,
            code: authorizationCode,
            code_verifier: sessionStorage.getItem(AUTH_CODE_VERIFIER_STORAGE_KEY) ?? undefined,
            redirect_uri: requestURL.origin + requestURL.pathname
        };

        const response = await fetch<AuthTokenResponseData>(options.tokenURL, {
            body: qs.stringify(data),
            headers: { 'Content-Type': AUTH_FORM_URL_ENCODED_CONTENT_TYPE },
            method: 'POST'
        });

        userSession.setTokens(response.access_token, response.refresh_token, response.expires_in);

        await updateUserInfo();
        publishAuthSessionEvent(AuthSessionEventTypes.SignedIn);

        if (redirect) {
            await navigateTo(redirect);
        }
    }

    async function updateUserInfo() {
        const response = await fetch<{ claims: UserInfo }>(options.userInfoURL, { method: 'GET' });
        userSession.setUserInfo(response.claims);
    }

    const resetPassword = async (data: ResetPasswordFormDTO) =>
        await fetch(options.resetPasswordURL, { body: data, method: 'POST' });

    const register = async (data: RegisterFormDTO) =>
        await fetch<RegisterResultDTO>(options.registerURL, { body: data, method: 'POST' });

    const confirmEmail = async (data: ConfirmEmailFormDTO) =>
        await fetch(options.confirmEmailURL, { body: data, method: 'POST' });

    const forgotPassword = async (data: ForgotPasswordFormDTO) =>
        await fetch(options.forgotPasswordURL, { body: data, method: 'POST' });

    async function signOut({ revokeRemoteSession = true, redirect }: SignOutOptions = {}) {
        if (revokeRemoteSession && userSession.isAuthenticated) {
            try {
                await fetch(options.logoutURL, { method: 'GET' });
            } catch {
                console.warn('Remote sign-out failed. Continuing with local sign-out.');
            }
        }

        userSession.clear();
        publishAuthSessionEvent(AuthSessionEventTypes.SignedOut);

        await navigateTo({
            path: options.logoutRedirectTo,
            query: { redirect }
        });
    }

    function isRouteAccessible(route: RouteRecordRaw | RouteLocationNormalized) {
        const meta = route.meta as PageRouteMeta | undefined;

        if (meta?.auth) {
            if (!userSession.isAuthenticated) {
                return false;
            }

            if (typeof meta.auth !== 'boolean') {
                return userSession.isInRole(meta.auth.roles);
            }
        }

        if (meta?.guestOnly && userSession.isAuthenticated) {
            return false;
        }

        return true;
    }

    function generateRandomString(length: number) {
        let text = '';

        for (let i = 0; i < length; i++) {
            text += AUTH_RANDOM_CHARACTERS.charAt(Math.floor(Math.random() * AUTH_RANDOM_CHARACTERS.length));
        }

        return text;
    }

    async function generateCodeChallenge(codeVerifier: string) {
        const data = new TextEncoder().encode(codeVerifier);
        const digest = await crypto.subtle.digest('SHA-256', data);

        const base64Encoded = btoa(String.fromCharCode(...new Uint8Array(digest)));

        // To base64URL
        return base64Encoded.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    }

    const fetch = async <T = unknown>(url: string, opts: ApiFetchOptions) =>
        await useNuxtApp().$apiFetch<T>(url, { ...opts, baseURL: authBaseURL });

    return {
        signIn,
        signInAsUser,
        signInBackAsAdmin,
        signInProvider,
        requestToken,
        register,
        confirmEmail,
        resetPassword,
        forgotPassword,
        signOut,
        updateUserInfo,
        isRouteAccessible
    };
};
